import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AdFileKind, adReviewComments } from '../../marketing-review-interfaces/marketing-review.interface';
import { PinViewerComponent } from '../pin-viewer/pin-viewer.component';

/** One version as the compare view needs it — the review page's `MediaTab` satisfies this. */
export interface CompareVersion {
    attachmentId: number | null;
    version: number;
    isCurrent: boolean;
    fileName: string;
    url: string;
    kind: AdFileKind;
    pinnable: boolean;
    icon: string;
    tone: string;
    label: string;
    uploadedOn: string;
}

type CompareSide = 'left' | 'right';

/** One side of the view, resolved once per change so the viewers are not handed new arrays each pass. */
interface ComparePane {
    side: CompareSide;
    version: CompareVersion;
    /** Pinned comments made on this version. */
    pins: adReviewComments[];
    openPins: number;
}

/**
 * Two versions of one file side by side, so a reviewer can check whether earlier feedback was
 * addressed. Each pane shows the pins made on its own version, read-only — commenting happens back
 * on the review page, against the current version.
 *
 * Zoom is shared, and scrolling is linked by default: each pane follows the other at the same
 * relative position, since two versions rarely run to the same length.
 */
@Component({
    selector: 'app-version-compare',
    imports: [MatFormFieldModule, MatIconModule, MatSelectModule, MatTooltipModule, PinViewerComponent],
    templateUrl: './version-compare.component.html',
    styleUrl: './version-compare.component.scss'
})
export class VersionCompareComponent implements OnChanges {

    /** Every version of the file, oldest first. */
    @Input({ required: true }) versions: CompareVersion[] = [];
    @Input({ required: true }) fileName: string = '';
    @Input() comments: adReviewComments[] = [];
    @Input() leftId: number | null = null;
    @Input() rightId: number | null = null;

    @Output() exit = new EventEmitter<void>();

    @ViewChildren('paneScroll') private paneScrolls?: QueryList<ElementRef<HTMLElement>>;

    protected panes: ComparePane[] = [];
    protected zoom: number = 100;
    protected linkedScroll: boolean = true;

    private readonly ZOOM_MIN: number = 50;
    private readonly ZOOM_MAX: number = 200;
    private readonly ZOOM_STEP: number = 25;

    private selected: Record<CompareSide, number | null> = { left: null, right: null };
    /** The pane whose next scroll event was caused by mirroring, not by the reviewer. */
    private mirroring: CompareSide | null = null;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['leftId'] || changes['rightId']) {
            this.selected = { left: this.leftId, right: this.rightId };
        }
        this.buildPanes();
    }

    protected choose(side: CompareSide, attachmentId: number): void {
        this.selected[side] = attachmentId;
        this.buildPanes();
    }

    /** The version open on the other side — offered but disabled in this side's dropdown. */
    protected otherId(side: CompareSide): number | null {
        return this.selected[side === 'left' ? 'right' : 'left'];
    }

    protected swapSides(): void {
        this.selected = { left: this.selected.right, right: this.selected.left };
        this.buildPanes();
    }

    private buildPanes(): void {
        const fallback: Record<CompareSide, CompareVersion | undefined> = {
            left: this.versions[this.versions.length - 2] ?? this.versions[0],
            right: this.versions[this.versions.length - 1]
        };

        this.panes = (['left', 'right'] as CompareSide[])
            .map((side) => {
                const version: CompareVersion | undefined =
                    this.versions.find((v) => v.attachmentId === this.selected[side]) ?? fallback[side];
                if (!version) {
                    return null;
                }
                this.selected[side] = version.attachmentId;
                // The page's comments are unset until its request returns.
                const pins: adReviewComments[] = (this.comments ?? [])
                    .filter((comment) => comment.pin && comment.documentId === version.attachmentId);
                return {
                    side,
                    version,
                    pins,
                    openPins: pins.filter((comment) => (comment.status ?? '').toLowerCase() !== 'resolved').length
                };
            })
            .filter((pane): pane is ComparePane => pane !== null);
    }

    // ── Zoom ────────────────────────────────────────────────────────────────────

    protected zoomIn(): void {
        this.zoom = Math.min(this.ZOOM_MAX, this.zoom + this.ZOOM_STEP);
    }

    protected zoomOut(): void {
        this.zoom = Math.max(this.ZOOM_MIN, this.zoom - this.ZOOM_STEP);
    }

    protected get canZoomIn(): boolean {
        return this.zoom < this.ZOOM_MAX;
    }

    protected get canZoomOut(): boolean {
        return this.zoom > this.ZOOM_MIN;
    }

    // ── Linked scroll ───────────────────────────────────────────────────────────

    protected toggleLinkedScroll(): void {
        this.linkedScroll = !this.linkedScroll;
        if (this.linkedScroll) {
            // Line the right pane up with the left straight away, rather than on the next scroll.
            this.onPaneScroll('left');
        }
    }

    /**
     * Mirrors one pane's position onto the other as a fraction of its scrollable height. The
     * mirrored pane fires its own scroll event, which is swallowed so the two do not chase each
     * other; a no-op move is skipped, since it would fire no event to swallow.
     */
    protected onPaneScroll(from: CompareSide): void {
        if (this.mirroring === from) {
            this.mirroring = null;
            return;
        }
        if (!this.linkedScroll) {
            return;
        }

        const to: CompareSide = from === 'left' ? 'right' : 'left';
        const source: HTMLElement | undefined = this.paneElement(from);
        const target: HTMLElement | undefined = this.paneElement(to);
        if (!source || !target) {
            return;
        }

        const sourceRange: number = source.scrollHeight - source.clientHeight;
        const targetRange: number = target.scrollHeight - target.clientHeight;
        const next: number = Math.round((sourceRange > 0 ? source.scrollTop / sourceRange : 0) * targetRange);

        if (Math.abs(target.scrollTop - next) < 1) {
            return;
        }
        this.mirroring = to;
        target.scrollTop = next;
    }

    private paneElement(side: CompareSide): HTMLElement | undefined {
        return this.paneScrolls?.get(side === 'left' ? 0 : 1)?.nativeElement;
    }
}
